import { useEffect, useRef, useState } from "react";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector } from "../../app/appHooks";
import { IAppState } from "../../../helpers/store";
import {
  changeFeedEnabled,
  listenToChangeFeed,
} from "../../../helpers/firebase";
import {
  applyChangeRecords,
  failedFirebaseDashboard,
  IChangeFeedState,
  ILoadActiveEventsArgs,
  isUnreadWarning,
  loadActiveEvents,
  refreshResolvedEvent,
  reloadChangeFeed,
  turnOffChangeFeed,
} from "../dashboardSlice";
import { eventsAllCommunities } from "../../../services/dashboard.services";
import useOrganization from "../activeNotificationPanel/hooks/useOrganization";

// A page asleep or offline longer than this loads again instead of catching up
const RELOAD_AFTER_MS = 10 * 60 * 1000;
const WAKE_CHECK_MS = 60 * 1000;
const RETRY_AFTER_ERROR_MS = 30 * 1000;
// A full warning list is loaded again once removals take it under this
const REFILL_BELOW = 80;
const REFILL_DEBOUNCE_MS = 3000;

// The listener starts from the earliest time any of the starting data was read
const changeFeedStart = (feed: IChangeFeedState) =>
  [
    feed.criticalAsOf,
    feed.warningAsOf,
    feed.resolvedAsOf,
    ...Object.values(feed.tileCounts).map((counts) => counts.version),
  ]
    .filter((asOf): asOf is string => !!asOf)
    .sort()[0];

// What the view on screen loads its starting lists for, undefined until its units have loaded
function useChangeFeedScope(allNotifications: boolean) {
  const { selectedOrganizationID, orgsCommunities } = useOrganization();

  const firebaseAuth = useAppSelector((state) => state.loginState.firebaseAuth);
  const installerRole = useAppSelector(
    (state) => state.headerState.installerRole
  );
  const userID = useAppSelector((state) => state.headerState.user?.id);
  const selectedCommunityID = useAppSelector(
    (state) => state.headerState.selectedCommunity?.id
  );
  const unitsLoaded = useAppSelector(
    (state) => state.dashboardState.units !== undefined
  );
  // Every unit of the organization's communities, once they have all loaded
  const orgUnitIDs = useAppSelector((state) => {
    if (orgsCommunities === undefined) return "";
    const units = orgsCommunities.map(
      (community) => state.dashboardState.orgUnits[community.id]
    );
    if (units.some((communityUnits) => communityUnits === undefined)) return "";
    return units
      .flat()
      .map((unit) => unit.id)
      .sort()
      .join(",");
  });
  // Every unit across the user's communities
  const allUnitIDs = useAppSelector((state) =>
    allNotifications
      ? Object.keys(state.dashboardState.allUnits).sort().join(",")
      : ""
  );

  const loadArgs: ILoadActiveEventsArgs | undefined = allNotifications
    ? allUnitIDs !== ""
      ? {
          scope: "all",
          eventScope: eventsAllCommunities
            ? { all_communities: 1 }
            : { unit_id: allUnitIDs },
          unitIDs: allUnitIDs.split(","),
          allNotifications: true,
        }
      : undefined
    : selectedCommunityID !== undefined
    ? {
        scope: selectedCommunityID,
        eventScope: { community_id: selectedCommunityID },
      }
    : selectedOrganizationID !== undefined && orgUnitIDs !== ""
    ? {
        scope: selectedOrganizationID,
        eventScope: { unit_id: orgUnitIDs },
        unitIDs: orgUnitIDs.split(","),
      }
    : undefined;

  return {
    loadArgs,
    loadKey: JSON.stringify(loadArgs ?? null),
    canLoad: firebaseAuth && !installerRole && userID !== undefined,
    installerRole,
    userID,
    selectedCommunityID,
    // A community's listener waits for its units, whose tile counts can be older than the lists
    unitsReady:
      allNotifications || selectedCommunityID === undefined || unitsLoaded,
  };
}

// Loads the view's starting lists from the API and follows the user's change feed.
// onCriticalAdded runs for each critical event created after the first snapshot
function useChangeFeed(
  onCriticalAdded: (event: { id: string; time_created: string }) => void,
  allNotifications: boolean = false
) {
  const dispatch = useAppDispatch();
  const store = useStore<IAppState>();
  const {
    loadArgs,
    loadKey,
    canLoad,
    installerRole,
    userID,
    selectedCommunityID,
    unitsReady,
  } = useChangeFeedScope(allNotifications);

  /* ---- Selectors ---- */
  const mode = useAppSelector((state) => state.dashboardState.changeFeed.mode);
  const warningsFull = useAppSelector(
    (state) => state.dashboardState.changeFeed.warningsFull
  );
  const refillingWarnings = useAppSelector(
    (state) => state.dashboardState.changeFeed.refillingWarnings
  );
  const refillFailed = useAppSelector(
    (state) => state.dashboardState.changeFeed.refillFailed
  );
  const unreadWarnings = useAppSelector(
    (state) =>
      state.dashboardState.communityEvents?.filter(isUnreadWarning).length ?? 0
  );

  const onCriticalAddedRef = useRef(onCriticalAdded);
  onCriticalAddedRef.current = onCriticalAdded;
  const [loadAttempt, setLoadAttempt] = useState(0);

  /* ---- Effects ---- */
  // Installers never load the feed, so their views keep the listeners
  useEffect(() => {
    if (changeFeedEnabled && installerRole && mode !== "off")
      dispatch(turnOffChangeFeed());
  }, [dispatch, installerRole, mode]);

  // Loads the starting lists, and the tile counts too when loading again.
  // A failed load, like one right after waking before the network is back, is tried again
  useEffect(() => {
    if (mode !== "loading" || !canLoad || loadArgs === undefined) return;
    const loadedBefore =
      store.getState().dashboardState.changeFeed.criticalAsOf !== undefined;
    let cancelled = false;
    let retryTimeout: number | undefined;
    dispatch(
      loadActiveEvents({
        ...loadArgs,
        communityID: loadedBefore ? selectedCommunityID : undefined,
      })
    ).then((result) => {
      if (
        cancelled ||
        !loadActiveEvents.rejected.match(result) ||
        store.getState().dashboardState.changeFeed.mode !== "loading"
      )
        return;
      retryTimeout = window.setTimeout(
        () => setLoadAttempt((attempt) => attempt + 1),
        RETRY_AFTER_ERROR_MS
      );
    });
    return () => {
      cancelled = true;
      window.clearTimeout(retryTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, canLoad, loadKey, loadAttempt]);

  const listenReady = mode === "on" && userID !== undefined && unitsReady;

  useEffect(() => {
    if (!listenReady || userID === undefined) return;
    const since = changeFeedStart(store.getState().dashboardState.changeFeed);

    let retryTimeout: number | undefined;
    const unsubscribe = listenToChangeFeed(
      userID,
      since,
      (records, initial) => {
        dispatch(applyChangeRecords(records));
        records.forEach((record) => {
          if (
            record.kind === "unresolved_removed" &&
            !record.event &&
            record.event_id
          )
            dispatch(refreshResolvedEvent(record.event_id));
        });
        // The first snapshot replays changes the starting lists already have
        if (initial) return;
        const { changeFeed, units } = store.getState().dashboardState;
        const unitIDs = new Set(
          changeFeed.unitIDs ?? units?.map((unit) => unit.id) ?? []
        );
        records.forEach((record) => {
          if (
            record.kind === "unresolved_added" &&
            record.event &&
            unitIDs.has(record.unit_id)
          )
            onCriticalAddedRef.current({
              id: record.event.id,
              time_created: record.event.time_created,
            });
        });
      },
      (error) => {
        console.error(error);
        dispatch(failedFirebaseDashboard());
        retryTimeout = window.setTimeout(
          () => dispatch(reloadChangeFeed()),
          RETRY_AFTER_ERROR_MS
        );
      }
    );
    return () => {
      unsubscribe();
      window.clearTimeout(retryTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listenReady, userID]);

  // Records only live about a day, so a page back from sleep or offline loads again
  useEffect(() => {
    if (mode !== "on") return;
    let lastCheck = Date.now();
    let offlineSince: number | null = null;

    const interval = window.setInterval(() => {
      const now = Date.now();
      if (now - lastCheck > RELOAD_AFTER_MS) dispatch(reloadChangeFeed());
      lastCheck = now;
    }, WAKE_CHECK_MS);
    const handleOffline = () => {
      offlineSince = Date.now();
    };
    const handleOnline = () => {
      if (offlineSince !== null && Date.now() - offlineSince > RELOAD_AFTER_MS)
        dispatch(reloadChangeFeed());
      offlineSince = null;
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [dispatch, mode]);

  // Removals never bring back warnings past the limit, so a full list is loaded again
  useEffect(() => {
    if (
      mode !== "on" ||
      !warningsFull ||
      refillingWarnings ||
      unreadWarnings >= REFILL_BELOW ||
      loadArgs === undefined
    )
      return;
    const timeout = window.setTimeout(
      () => dispatch(loadActiveEvents({ ...loadArgs, warningsOnly: true })),
      refillFailed ? RETRY_AFTER_ERROR_MS : REFILL_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, warningsFull, refillingWarnings, unreadWarnings, loadKey]);

  return mode;
}

export default useChangeFeed;
